import { useMemo } from "react";
import { Box, type SxProps } from "@mui/material";
import { useTheme, type Theme } from "@mui/material/styles";
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridValidRowModel,
} from "@mui/x-data-grid";
import OverflowTooltip from "@components/OverflowTooltip/OverflowTooltip";
import { t } from "i18next";
import { renderColumns } from "./columns";
import type { BaseTableProps } from "./CustomDataTable.types";
import { dataGridSx, dataGridWrapperSx } from "./styles";

type BaseTableExtra<T> = {
  onRowEdit?: (row: T) => void;
  onRowDelete?: (row: T) => void;
  onRowClick?: (row: T, e: any) => void;
  customSx?: SxProps<Theme>;
};

const getCellTooltipTitle = (params: GridRenderCellParams) => {
  const value = params.formattedValue ?? params.value;
  return value == null || value === "" ? undefined : String(value);
};

function TableCellOverflow({
  children,
  fallbackTitle,
}: {
  children: React.ReactNode;
  fallbackTitle?: string;
}) {
  return (
    <OverflowTooltip
      placement="top"
      title={(el) => el?.textContent?.trim() || fallbackTitle || ""}
      getTarget={(wrapperEl) => {
        const target = wrapperEl.querySelector(
          "[data-overflow-tooltip-target='true']",
        ) as HTMLElement | null;
        return (target?.firstElementChild as HTMLElement | null) || target;
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          minWidth: 0,
          height: "100%",
          fontWeight: "inherit",
        }}
      >
        <Box
          data-overflow-tooltip-target="true"
          sx={{
            "display": "block",
            "width": "100%",
            "minWidth": 0,
            "overflow": "hidden",
            "textOverflow": "ellipsis",
            "whiteSpace": "nowrap",
            "fontWeight": "inherit",
            "& > *": {
              display: "block",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
              fontWeight: "inherit",
            },
            "& .MuiChip-root": {
              maxWidth: "100%",
            },
            "& .MuiBadge-root": {
              maxWidth: "100%",
            },
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </OverflowTooltip>
  );
}

function withOverflowTooltip<T extends GridValidRowModel>(column: GridColDef<T>): GridColDef<T> {
  const nonOverflowFields = new Set(["action", "__action__", "__select"]);

  if (nonOverflowFields.has(String(column.field))) {
    return column;
  }

  const originalRenderCell = column.renderCell;

  return {
    ...column,
    display: column.display ?? "flex",
    renderCell: (params: GridRenderCellParams<T>) => {
      const content = originalRenderCell
        ? originalRenderCell(params)
        : (params.formattedValue ?? params.value ?? "-");

      return (
        <TableCellOverflow fallbackTitle={getCellTooltipTitle(params)}>{content}</TableCellOverflow>
      );
    },
  };
}

const BaseTable = <T extends GridValidRowModel>({
  rows,
  headers,
  customSx,
  onRowClick,
  onRowEdit,
  onRowDelete,
}: BaseTableProps<T> & BaseTableExtra<T>) => {
  const theme = useTheme();

  const columns = useMemo(
    () =>
      renderColumns(headers, rows, theme, undefined, {
        onEdit: onRowEdit,
        onDelete: onRowDelete,
      }).map((column) => withOverflowTooltip(column)),
    [headers, rows, theme, onRowEdit, onRowDelete, onRowClick],
  );
  const sx = (customSx ? [dataGridSx, customSx] : dataGridSx) as SxProps<Theme>;

  return (
    <Box sx={dataGridWrapperSx}>
      <DataGrid
        rows={rows}
        columns={columns}
        hideFooter
        disableColumnMenu
        disableColumnSorting
        disableColumnResize
        localeText={{ noRowsLabel: t("common.empty-table.no-rows") }}
        onRowClick={(params, e) => {
          if (onRowClick) {
            onRowClick(params.row, e);
          }
        }}
        getRowId={(row) => row.id}
        sx={sx}
      />
    </Box>
  );
};

export default BaseTable;
